import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBars, faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '~/App';
import { BrandSquareMono } from '~/brand/BrandContext';
import { LogoutLink } from '~/components/auth/LogoutLink';
import { isUserSettingsHash } from '~/components/users/settings/user/settingsHash';
import { PoweredBy } from '~/layouts/nav/MoreNav';
import { SettingsLink } from '~/layouts/nav/SettingsLink';
import { SupportLink } from '~/layouts/nav/SupportLink';
import { TenantSwitcher } from '~/layouts/nav/TenantSwitcher';
import { CurrentAvatar } from './Avatar';
import { CountBadge } from './CountBadge';
import { SideSheet } from './nucleus/Sheets';
import { BottomSheet } from './nucleus/Sheets/BottomSheet';

export type MenuItem = {
  key: string;
  label: React.ReactNode;
  to?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  badge?: number;
  isActive?: boolean;
  isConfigItem?: boolean;
};

type ConfigSectionProps = {
  items: MenuItem[];
  renderMenuItem: (item: MenuItem, showBorder?: boolean) => React.ReactNode;
};

const ConfigSection = ({ items, renderMenuItem }: ConfigSectionProps) => {
  const { t } = useTranslation();

  // Filter out config separator and only show actual config items
  const configItems = items.filter((item) => item.key !== 'config-separator');

  if (configItems.length === 0) return null;

  return (
    <div className='mx-4 mt-4 rounded-lg border border-white border-opacity-10 bg-white bg-opacity-5'>
      <div className='text-md flex items-center gap-2 border-b border-white border-opacity-20 bg-white/10 px-6 pb-4 pt-[18px] font-medium text-white'>
        <FontAwesomeIcon icon={faCog as IconProp} className='h-4 w-4' />
        <span className='text-sm uppercase'>{t('config')}</span>
      </div>
      {configItems.map((item) => (
        <div className='last:pb-1 [&:nth-child(2)]:pt-1' key={item.key}>
          {renderMenuItem(item, false)}
        </div>
      ))}
    </div>
  );
};

type TopBarProps = {
  title: React.ReactNode;
  onMenuClick?: () => void;
  menuItems?: MenuItem[];
  onMenuItemClick?: (item: MenuItem) => void;
};

export const TopBar = ({
  title,
  onMenuClick,
  menuItems,
  onMenuItemClick,
}: TopBarProps) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppContext();

  const handleMenuClick = () => {
    if (onMenuClick) {
      requestAnimationFrame(() => {
        onMenuClick();
      });
    } else if (menuItems) {
      setIsMenuOpen(true);
    }
  };

  // Helper functions for settings navigation
  const openUserSettings = () => navigate('#user-settings-open');
  const openAccountSettings = () => navigate('#org-settings-open');

  const renderMenuItem = (item: MenuItem, showBorder: boolean = true) => {
    const isActive =
      item.isActive || (item.to && location.pathname.startsWith(item.to));

    const content = (
      <div className='flex w-full items-center justify-between px-6 py-4'>
        <div className='flex items-center'>
          <span className={isActive ? 'underline' : ''}>{item.label}</span>
          {item.badge !== undefined && item.badge > 0 && (
            <CountBadge className='ml-2' count={item.badge} />
          )}
        </div>
        {item.icon && <span className='ml-2'>{item.icon}</span>}
      </div>
    );

    const handleClick = (e?: MouseEvent) => {
      // Only intercept plain primary (left) clicks without modifiers
      if (e) {
        const isPlainPrimary =
          e.button === 0 &&
          !e.metaKey &&
          !e.ctrlKey &&
          !e.shiftKey &&
          !e.altKey;
        if (isPlainPrimary) {
          e.preventDefault();
          e.stopPropagation();
        } else {
          // Allow modified clicks to open in new tabs/windows normally
          return;
        }
      }
      requestAnimationFrame(() => {
        if (item.onClick) item.onClick();
        if (onMenuItemClick) onMenuItemClick(item);
        // If this item targets settings via hash, open the profile sheet first
        const to = item.to || '';
        const opensSettings =
          to.startsWith('#org-settings-open') ||
          to.startsWith('#user-settings-open');
        if (opensSettings) {
          setIsProfileOpen(true);
        }
        // Navigate programmatically to ensure controlled timing on mobile (iOS Safari)
        if (to) {
          navigate(to);
        }
        // Delay closing slightly so the overlay absorbs the tap and prevents pass-through
        window.setTimeout(() => setIsMenuOpen(false), 120);
      });
    };

    // Add border based on showBorder parameter
    const borderClass = showBorder
      ? 'border-b border-white border-opacity-15'
      : '';

    if (item.to) {
      return (
        <Link
          key={item.key}
          to={item.to}
          onClick={handleClick}
          className={classNames(
            'block whitespace-nowrap text-lg text-white underline-offset-4',
            borderClass
          )}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        key={item.key}
        onClick={handleClick}
        className={classNames(
          'block w-full whitespace-nowrap text-left text-lg text-white underline-offset-4',
          borderClass
        )}
      >
        {content}
      </button>
    );
  };

  // Determine if any settings sheet is open using current URL hash to
  // avoid timing issues with hook state updates during programmatic navigation
  const isSettingsOpen =
    isUserSettingsHash(location.hash) ||
    location.hash.startsWith('#org-settings-open');

  return (
    <>
      <div className='relative my-auto grid h-16 grid-cols-[45px_minmax(0,_1fr)_45px] overflow-x-auto bg-primary-container py-2 pl-2 pr-3'>
        {onMenuClick || menuItems ? (
          <button
            className='flex items-center justify-center p-2 text-white hover:opacity-80 focus:opacity-80'
            onClick={handleMenuClick}
          >
            <FontAwesomeIcon icon={faBars} className='h-6 w-6' />
          </button>
        ) : (
          <Link to='/' className='ml-2 flex items-center'>
            <BrandSquareMono className='h-11 w-11' />
          </Link>
        )}
        <div className='flex items-center justify-center px-2'>
          {typeof title === 'string' ? (
            <h1 className='max-w-full truncate text-center text-xl font-medium text-white'>
              {title}
            </h1>
          ) : (
            <div className='max-w-full text-center text-xl font-medium text-white'>
              {title}
            </div>
          )}
        </div>
        <div className='flex items-center justify-center'>
          <button
            onClick={() => setIsProfileOpen(true)}
            className='flex h-10 w-10 items-center justify-center'
          >
            <CurrentAvatar size='xl' />
          </button>
        </div>
      </div>

      {/* Side menu */}
      {menuItems && (
        <SideSheet
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          subnav={true}
        >
          <div className='flex h-full flex-col overflow-hidden'>
            <div className='flex flex-1 flex-col overflow-y-auto'>
              {/* Regular navigation items */}
              {menuItems
                .filter((item) => !item.isConfigItem)
                .map((item, index) => (
                  <div
                    key={item.key}
                    className={
                      index > 0 ? 'border-t border-white border-opacity-15' : ''
                    }
                  >
                    {renderMenuItem(item)}
                  </div>
                ))}
            </div>

            {/* Config section */}
            <ConfigSection
              items={menuItems.filter((item) => item.isConfigItem)}
              renderMenuItem={renderMenuItem}
            />
          </div>
        </SideSheet>
      )}

      {/* User profile bottom sheet with nested settings sheet */}
      <BottomSheet
        isOpen={isProfileOpen}
        onClose={() => {
          setIsProfileOpen(false);
        }}
        title='Accounts'
        stacked={isSettingsOpen}
        hasDivider
      >
        <ul className='w-full pb-4'>
          <li className='border-b border-outline-bright px-4 pb-5'>
            <h2 className='mb-2.5 mt-4 font-medium leading-5 text-secondary'>
              Personal
            </h2>
            <div className='flex items-center justify-between gap-4 pl-0.5'>
              <div className='flex items-center gap-4 pl-0.5'>
                <CurrentAvatar size='xl' />
                <div className='flex flex-col'>
                  <span className='text-lg font-medium'>{user?.name}</span>
                  <span className='text-sm text-secondary'>{user?.email}</span>
                </div>
              </div>
              <SettingsLink onClick={openUserSettings} iconButton />
            </div>
          </li>
          <li className='border-b border-outline-bright px-4 pb-5'>
            <h2 className='mb-2.5 mt-4 font-medium leading-5 text-secondary'>
              Organisation
            </h2>
            <div className='flex items-center justify-between gap-3'>
              <TenantSwitcher />
              <SettingsLink onClick={openAccountSettings} iconButton />
            </div>
          </li>
          <li className='cursor-pointer px-4 pt-4 text-lg text-secondary'>
            <SupportLink />
          </li>
          <li className='cursor-pointer px-6 text-lg text-secondary'>
            <LogoutLink />
          </li>
        </ul>
        <PoweredBy />
      </BottomSheet>
    </>
  );
};
